import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';

const Card = styled.div`
  background-color: ${({ theme }) => theme.colors.background};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  box-shadow: ${({ theme }) => theme.shadows.md};
  margin-bottom: ${({ theme }) => theme.spacing.lg};
  overflow: hidden;
  transition: transform ${({ theme }) => theme.transitions.fast}, 
              box-shadow ${({ theme }) => theme.transitions.fast};
  
  &:hover {
    transform: translateY(-4px);
    box-shadow: ${({ theme }) => theme.shadows.lg};
  }
`;

const CardMedia = styled.div`
  position: relative;
  width: 100%;
  height: 0;
  padding-bottom: 56.25%; /* 16:9 aspect ratio */
  background-color: ${({ theme }) => theme.colors.cardBg};
  overflow: hidden;
  
  img, video {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const CardContent = styled.div`
  padding: ${({ theme }) => theme.spacing.lg};
`;

const GroupInfo = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: ${({ theme }) => theme.spacing.sm};
`;

const GroupIcon = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSizes.lg};
  margin-right: ${({ theme }) => theme.spacing.xs};
`;

const GroupName = styled(Link)`
  font-size: ${({ theme }) => theme.typography.fontSizes.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeights.medium};
  color: ${({ theme }) => theme.colors.text.secondary};
  
  &:hover {
    color: ${({ theme }) => theme.colors.primary};
  }
`;

const PostTitle = styled(Link)`
  display: block;
  font-size: ${({ theme }) => theme.typography.fontSizes.xl};
  font-weight: ${({ theme }) => theme.typography.fontWeights.semibold};
  color: ${({ theme }) => theme.colors.text.primary};
  margin-bottom: ${({ theme }) => theme.spacing.sm};
  
  &:hover {
    color: ${({ theme }) => theme.colors.primary};
  }
`;

const PostDescription = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSizes.md};
  color: ${({ theme }) => theme.colors.text.secondary};
  margin-bottom: ${({ theme }) => theme.spacing.md};
  line-height: ${({ theme }) => theme.typography.lineHeights.normal};
`;

const PostMeta = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

const UserAvatar = styled(Link)`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  overflow: hidden;
  margin-right: ${({ theme }) => theme.spacing.sm};
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const UserInfo = styled.div`
  display: flex;
  flex-direction: column;
`;

const UserName = styled(Link)`
  font-size: ${({ theme }) => theme.typography.fontSizes.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeights.semibold};
  color: ${({ theme }) => theme.colors.text.primary};
  
  &:hover {
    color: ${({ theme }) => theme.colors.primary};
  }
`;

const PostTime = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSizes.xs};
  color: ${({ theme }) => theme.colors.text.tertiary};
`;

const UserRank = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSizes.xs};
  font-weight: ${({ theme }) => theme.typography.fontWeights.medium};
  color: white;
  background: ${({ theme }) => theme.gradients.accent};
  padding: ${({ theme }) => theme.spacing.xs} ${({ theme }) => theme.spacing.sm};
  border-radius: ${({ theme }) => theme.borderRadius.full};
  margin-left: ${({ theme }) => theme.spacing.sm};
`;

const PostActions = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-top: 1px solid ${({ theme }) => theme.colors.border};
  padding-top: ${({ theme }) => theme.spacing.md};
`;

const ActionButton = styled.button`
  display: flex;
  align-items: center;
  color: ${({ theme, active }) => active ? theme.colors.primary : theme.colors.text.secondary};
  font-size: ${({ theme }) => theme.typography.fontSizes.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeights.medium};
  
  &:hover {
    color: ${({ theme }) => theme.colors.primary};
  }
  
  span {
    margin-left: ${({ theme }) => theme.spacing.xs};
  }
`;

const JoinGroupButton = styled(Link)`
  display: inline-block;
  background: ${({ theme }) => theme.gradients.primary};
  color: white;
  font-size: ${({ theme }) => theme.typography.fontSizes.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeights.medium};
  padding: ${({ theme }) => theme.spacing.xs} ${({ theme }) => theme.spacing.md};
  border-radius: ${({ theme }) => theme.borderRadius.full};
  margin-top: ${({ theme }) => theme.spacing.sm};
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: ${({ theme }) => theme.shadows.md};
    color: white;
  }
`;

const PostCard = ({ post }) => {
  const [liked, setLiked] = useState(false);
  const [likeCount, setLikeCount] = useState(post.likes);

  const handleLike = () => {
    if (liked) {
      setLikeCount(likeCount - 1);
    } else {
      setLikeCount(likeCount + 1);
    }
    setLiked(!liked);
  };

  return (
    <Card>
      {post.media && (
        <CardMedia>
          {post.mediaType === 'image' ? (
            <img src={post.media} alt={post.title} />
          ) : (
            <video src={post.media} poster={post.thumbnail} controls />
          )}
        </CardMedia>
      )}

      <CardContent>
        <GroupInfo>
          <GroupIcon>{post.group.icon}</GroupIcon>
          <GroupName to={`/group/${post.group.id}`}>{post.group.name}</GroupName>
          {post.showJoinButton && (
            <JoinGroupButton to={`/group/${post.group.id}`}>Join Group</JoinGroupButton>
          )}
        </GroupInfo>

        <PostTitle to={`/post/${post.id}`}>{post.title}</PostTitle>
        <PostDescription>{post.description}</PostDescription>

        <PostMeta>
          <UserAvatar to={`/profile/${post.user.username}`}>
            <img src={post.user.avatar} alt={post.user.name} />
          </UserAvatar>
          <UserInfo>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <UserName to={`/profile/${post.user.username}`}>{post.user.name}</UserName>
              <UserRank>{post.user.rank}</UserRank>
            </div>
            <PostTime>{post.timeAgo}</PostTime>
          </UserInfo>
        </PostMeta>

        <PostActions>
          <ActionButton active={liked} onClick={handleLike}>
            {liked ? '❤️' : '🤍'} <span>{likeCount}</span>
          </ActionButton>
          <ActionButton>
            💬 <span>{post.comments}</span>
          </ActionButton>
          <ActionButton>
            🔄 <span>Share</span>
          </ActionButton>
          <ActionButton>
            🔖 <span>Save</span>
          </ActionButton>
        </PostActions>
      </CardContent>
    </Card>
  );
};

export default PostCard;
